import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from '@/shared/components/ui/sonner';
import { TooltipProvider } from '@/shared/components/ui/tooltip';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './index.css';

import App from './App';
import { i18n } from '@/shared/lib/i18n';
import { initSentry } from '@/shared/lib/sentry';
import { initPosthog } from '@/shared/lib/posthog';
import { ThemeProvider } from '@/shared/hooks/useTheme';
import { AuthProvider } from '@/shared/auth/AuthContext';

initSentry();
initPosthog();

// A tab left open across a deploy still references the previous build's
// hashed chunks, which no longer exist on the server: every lazy page then
// fails with a 404 and lands in ErrorBoundary. Reload once to pick up the new
// build. The timestamp guard stops a reload loop if the chunk is missing for
// another reason (network down, broken deploy) — the error then propagates.
const CHUNK_RELOAD_KEY = 'vg:chunk-reload-at';
window.addEventListener('vite:preloadError', (event) => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReloadAt < 10_000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider>
              {/* One provider for the whole app: after the first tooltip, the
                  next ones open instantly (skipDelayDuration). */}
              <TooltipProvider delayDuration={300} skipDelayDuration={400}>
                <App />
                <Toaster position="top-right" closeButton />
              </TooltipProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </I18nextProvider>
  </React.StrictMode>,
);

// Mobile field app (PWA) service worker — scoped to /m/, so it never touches
// the operator app shell. Registered globally; harmless on operator pages.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/m/sw.js', { scope: '/m/' })
      .catch(() => {
        /* SW registration is best-effort; the app works without it */
      });
  });
}
